import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, videos } from '@/mocks/data';

const getCatName = (slug: string) => categories.find(c => c.slug === slug)?.name || slug;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const categoryVideos = videos.filter(v => v.categorySlug === activeSlug);
  const displayVideos = categoryVideos.length > 0 ? categoryVideos : videos.filter(v => v.categorySlug === categories[0].slug);

  return (
    <main>
      <Breadcrumb items={[
        { label: '分类浏览', href: '/category/dance' },
        { label: currentCategory?.name || activeSlug },
      ]} />

      <section className="w-full py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 font-heading">
            {currentCategory?.name || '分类'}视频
          </h1>
          {currentCategory && (
            <p className="text-sm text-foreground-500 mb-8">{currentCategory.description}</p>
          )}

          <div className="flex items-center gap-2 mb-10 flex-wrap">
            <div className="inline-flex items-center bg-background-100 rounded-full px-1 py-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full transition-all ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayVideos.map((video) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute bottom-2 right-2 bg-foreground-950/75 text-background-50 text-xs px-2 py-0.5 rounded">{video.duration}</div>
                  <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/20 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-play-fill text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 leading-snug">{video.title}</h3>
                  <div className="flex items-center gap-2">
                    <img src={video.authorAvatar} alt={video.author} className="w-6 h-6 rounded-full object-cover" loading="lazy" />
                    <span className="text-xs text-foreground-500">{video.author}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}</span>
                    <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{video.likes >= 10000 ? `${(video.likes / 10000).toFixed(0)}万` : video.likes}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}